import { Button, Intent } from "@blueprintjs/core";
import { segmentTrackUnlockBusConfirmationShown } from "analytics/analyticTrackEvents";
import DetailPaneMessageCard from "components/detailPane/shared/DetailPaneMessageCard";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import useSelectedScopedBus from "contexts/SelectedScopedBusContext/useSelectedScopedBus";
import React, { useEffect } from "react";
import BusDetailPane from "./BusDetailPane";
import { getBusSizeTags } from "./busSizeTags";
import ConnectedLinesSection from "./ConnectedLinesSection";

type UnlockPromptBusDetailPaneProps = {
    /** Buses left on the subscription *before* this bus is unlocked. */
    readonly busesRemaining: number;
    readonly numBuses: number;
    /**
     * False when the fresh status check failed, so the counts above came from
     * login-time data and we could not check whether the team has already
     * unlocked this bus. The question is then asked without a count: quoting
     * one would tell a team they are about to spend a bus on one they may
     * already own, and talk them out of opening a bus they have paid for.
     */
    readonly isStatusVerified: boolean;
    readonly onUnlock: () => void;
};

/**
 * The detail pane for a bus a bus-limited team hasn't unlocked yet: everything
 * about the bus we can show for free -- the same name, sizes and costs the
 * sidebar already lists, plus the bus details block -- with the study results
 * replaced by the question of whether to spend a bus on it.
 *
 * Dismissing is the pane's own close button (or picking another bus): nothing is
 * spent unless the user presses Unlock bus.
 */
const UnlockPromptBusDetailPane: React.FC<UnlockPromptBusDetailPaneProps> = (
    props
) => {
    const { busesRemaining, numBuses, isStatusVerified, onUnlock } = props;
    const { generator, componentConfig } = useMapViewRoutingMetadata();
    const scopedBus = useSelectedScopedBus();
    const { bus, scopedCapacityEnergyCost } = scopedBus;

    useEffect(() => {
        segmentTrackUnlockBusConfirmationShown(bus.niraBusId, busesRemaining);
    }, [bus.niraBusId, busesRemaining]);

    const { energyTag, chargingTag, capacityTag } = getBusSizeTags({
        scopedCapacityEnergyCost,
        componentConfig,
        generator
    });

    return (
        <BusDetailPane
            energyTag={energyTag}
            chargingTag={chargingTag}
            capacityTag={capacityTag}
            constraintDetailsSections={[
                <DetailPaneMessageCard
                    heading="Would you like to unlock this bus?"
                    description={
                        isStatusVerified ? (
                            <>
                                You have{" "}
                                <strong>
                                    {busesRemaining} of {numBuses} buses
                                </strong>{" "}
                                left in your {bus.region} subscription.
                            </>
                        ) : (
                            <>
                                This uses one of the buses in your {bus.region}{" "}
                                subscription, unless your team has already
                                unlocked this one.
                            </>
                        )
                    }
                    action={
                        <Button
                            text="Unlock bus"
                            intent={Intent.PRIMARY}
                            onClick={onUnlock}
                        />
                    }
                />
            ]}
            connectedLinesSection={<ConnectedLinesSection isLocked />}
            canAccessLocalStudies={false}
        />
    );
};

export default UnlockPromptBusDetailPane;
